import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import {
  Gift, Users, Calendar, Trophy, Search, Filter,
  ChevronDown, ChevronUp, Download, FileSpreadsheet,
  CircleAlert as AlertCircle, User
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

interface Inscrito {
  id: string;
  inscrito_em: string;
  usuario: {
    id: string;
    nome: string;
    email: string;
    setor: string;
  };
}

interface BrindeReport {
  id: string;
  titulo: string;
  descricao: string;
  data_sorteio: string;
  quantidade: number;
  status: 'ativo' | 'sorteado' | 'cancelado';
  inscricoes: Inscrito[];
  vencedores: { usuario_id: string; usuario: { nome: string } }[];
}

const statusConfig: Record<string, { label: string; color: string; bg: string; border: string }> = {
  ativo: { label: 'Ativo', color: 'text-green-700 dark:text-green-300', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-700' },
  sorteado: { label: 'Sorteado', color: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-700' },
  cancelado: { label: 'Cancelado', color: 'text-red-700 dark:text-red-300', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-700' },
};

export default function RelatorioInscricoesBrindes() {
  const { user } = useAuth();
  const [brindes, setBrindes] = useState<BrindeReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [selectedBrinde, setSelectedBrinde] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const isAdmin = user?.role === 'admin' || user?.role === 'moderador' || user?.setor === 'RH';

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const { data: brindesData, error: brindesError } = await supabase
        .from('sorteio_brindes')
        .select('id, titulo, descricao, data_sorteio, quantidade, status')
        .order('data_sorteio', { ascending: false });

      if (brindesError) throw brindesError;
      if (!brindesData) { setBrindes([]); return; }

      const result: BrindeReport[] = await Promise.all(
        brindesData.map(async (b) => {
          const { data: inscricoes } = await supabase
            .from('sorteio_inscricoes')
            .select('id, inscrito_em, usuario:usuario_id(id, nome, email, setor)')
            .eq('brinde_id', b.id)
            .order('inscrito_em', { ascending: true });

          const { data: vencedores } = await supabase
            .from('sorteio_vencedores')
            .select('usuario_id, usuario:usuario_id(nome)')
            .eq('brinde_id', b.id);

          return {
            ...b,
            inscricoes: (inscricoes || []) as unknown as Inscrito[],
            vencedores: (vencedores || []) as unknown as BrindeReport['vencedores'],
          };
        })
      );

      setBrindes(result);
    } catch (err: any) {
      toast.error('Erro ao carregar relatorio.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredBrindes = useMemo(() => {
    let list = brindes;
    if (statusFilter !== 'todos') {
      list = list.filter((b) => b.status === statusFilter);
    }
    if (selectedBrinde !== 'todos') {
      list = list.filter((b) => b.id === selectedBrinde);
    }
    return list;
  }, [brindes, statusFilter, selectedBrinde]);

  const filteredWithSearch = useMemo(() => {
    if (!searchTerm.trim()) return filteredBrindes;
    const term = searchTerm.toLowerCase();
    return filteredBrindes.map((b) => ({
      ...b,
      inscricoes: b.inscricoes.filter(
        (i) =>
          i.usuario.nome.toLowerCase().includes(term) ||
          i.usuario.email.toLowerCase().includes(term) ||
          i.usuario.setor.toLowerCase().includes(term)
      ),
    })).filter((b) => b.inscricoes.length > 0);
  }, [filteredBrindes, searchTerm]);

  const totalInscritos = useMemo(
    () => filteredWithSearch.reduce((acc, b) => acc + b.inscricoes.length, 0),
    [filteredWithSearch]
  );

  const toggleCard = (id: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedCards(new Set(filteredWithSearch.map((b) => b.id)));
  };

  const collapseAll = () => {
    setExpandedCards(new Set());
  };

  const isWinner = (brindeId: string, usuarioId: string) => {
    const b = brindes.find((br) => br.id === brindeId);
    return b?.vencedores.some((v) => v.usuario_id === usuarioId) ?? false;
  };

  const exportToExcel = () => {
    const rows: Record<string, string>[] = [];
    filteredWithSearch.forEach((b) => {
      b.inscricoes.forEach((i) => {
        rows.push({
          'Brinde': b.titulo,
          'Status': statusConfig[b.status]?.label ?? b.status,
          'Data Sorteio': format(new Date(b.data_sorteio + 'T12:00:00'), 'dd/MM/yyyy'),
          'Participante': i.usuario.nome,
          'Email': i.usuario.email,
          'Setor': i.usuario.setor,
          'Data Inscricao': format(new Date(i.inscrito_em), 'dd/MM/yyyy HH:mm', { locale: ptBR }),
          'Vencedor': isWinner(b.id, i.usuario.id) ? 'Sim' : 'Nao',
        });
      });
    });

    if (rows.length === 0) {
      toast.error('Nenhum dado para exportar.');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 30 }, { wch: 12 }, { wch: 14 }, { wch: 30 },
      { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inscricoes Brindes');
    XLSX.writeFile(wb, `relatorio-inscricoes-brindes-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Relatorio exportado com sucesso!');
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-on-surface font-medium">Acesso restrito a administradores.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Relatorio de Inscricoes</h1>
            <p className="text-secondary text-sm mt-1">Visualize todos os participantes inscritos em cada brinde</p>
          </div>
          <button
            onClick={exportToExcel}
            disabled={totalInscritos === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total de Brindes', value: filteredWithSearch.length, icon: Gift, color: 'blue' },
            { label: 'Total de Inscricoes', value: totalInscritos, icon: Users, color: 'green' },
            { label: 'Brindes Ativos', value: brindes.filter((b) => b.status === 'ativo').length, icon: Calendar, color: 'amber' },
            { label: 'Ja Sorteados', value: brindes.filter((b) => b.status === 'sorteado').length, icon: Trophy, color: 'teal' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 bg-${color}-100 dark:bg-${color}-900/30`}>
                <Icon className={`w-5 h-5 text-${color}-600`} />
              </div>
              <div className="text-2xl font-bold text-on-surface">{value}</div>
              <div className="text-xs text-secondary mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3 text-sm font-medium text-on-surface">
            <Filter className="w-4 h-4" />
            Filtros
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-secondary mb-1.5">Status do Brinde</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="todos">Todos os status</option>
                <option value="ativo">Ativos</option>
                <option value="sorteado">Sorteados</option>
                <option value="cancelado">Cancelados</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary mb-1.5">Brinde</label>
              <select
                value={selectedBrinde}
                onChange={(e) => setSelectedBrinde(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="todos">Todos os brindes</option>
                {brindes
                  .filter((b) => statusFilter === 'todos' || b.status === statusFilter)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.titulo} ({b.inscricoes.length} inscritos)
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary mb-1.5">Buscar Participante</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Nome, email ou setor..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Expand/Collapse */}
        {filteredWithSearch.length > 0 && (
          <div className="flex justify-end gap-2">
            <button onClick={expandAll} className="text-xs text-primary hover:text-primary-dark font-medium flex items-center gap-1">
              <ChevronDown className="w-3.5 h-3.5" /> Expandir todos
            </button>
            <span className="text-secondary">|</span>
            <button onClick={collapseAll} className="text-xs text-primary hover:text-primary-dark font-medium flex items-center gap-1">
              <ChevronUp className="w-3.5 h-3.5" /> Recolher todos
            </button>
          </div>
        )}

        {/* Report Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredWithSearch.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm text-center py-16">
            <Gift className="w-12 h-12 text-secondary mx-auto mb-3" />
            <p className="text-on-surface font-medium">Nenhum brinde encontrado</p>
            <p className="text-secondary text-sm mt-1">Ajuste os filtros para ver resultados.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredWithSearch.map((b) => {
              const isExpanded = expandedCards.has(b.id);
              const cfg = statusConfig[b.status] || statusConfig.ativo;
              const winnerIds = new Set(b.vencedores.map((v) => v.usuario_id));

              return (
                <div key={b.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                  <button
                    onClick={() => toggleCard(b.id)}
                    className="w-full text-left px-5 py-4 hover:bg-surface-container-low transition-colors"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Gift className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-on-surface">{b.titulo}</span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
                              {cfg.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-secondary mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {format(new Date(b.data_sorteio + 'T12:00:00'), 'dd/MM/yyyy')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              {b.inscricoes.length} inscrito{b.inscricoes.length !== 1 ? 's' : ''}
                            </span>
                            <span className="flex items-center gap-1">
                              <Gift className="w-3.5 h-3.5" />
                              {b.quantidade} vaga{b.quantidade !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-2xl font-bold text-secondary">{b.inscricoes.length}</span>
                        {isExpanded
                          ? <ChevronUp className="w-5 h-5 text-secondary" />
                          : <ChevronDown className="w-5 h-5 text-secondary" />
                        }
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-outline-variant">
                      {b.inscricoes.length === 0 ? (
                        <div className="text-center py-8 text-secondary text-sm">
                          Nenhum participante inscrito neste brinde.
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-12 gap-2 px-5 py-2.5 bg-surface-container text-xs font-medium text-secondary uppercase tracking-wider border-b border-outline-variant">
                            <div className="col-span-1">#</div>
                            <div className="col-span-4">Participante</div>
                            <div className="col-span-3 hidden sm:block">Email</div>
                            <div className="col-span-2">Setor</div>
                            <div className="col-span-2 text-right">Inscricao</div>
                          </div>
                          <div className="divide-y divide-outline-variant">
                            {b.inscricoes.map((i, idx) => {
                              const won = winnerIds.has(i.usuario.id);
                              return (
                                <div
                                  key={i.id}
                                  className={`grid grid-cols-12 gap-2 px-5 py-3 items-center text-sm transition-colors ${
                                    won ? 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30' : 'hover:bg-surface-container-low'
                                  }`}
                                >
                                  <div className="col-span-1 text-secondary text-xs font-medium">{idx + 1}</div>
                                  <div className="col-span-4 flex items-center gap-2.5 min-w-0">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                      won ? 'bg-amber-200 dark:bg-amber-800' : 'bg-surface-container'
                                    }`}>
                                      {won
                                        ? <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-300" />
                                        : <User className="w-4 h-4 text-secondary" />
                                      }
                                    </div>
                                    <div className="min-w-0">
                                      <span className={`font-medium truncate block ${won ? 'text-amber-800 dark:text-amber-300' : 'text-on-surface'}`}>
                                        {i.usuario.nome}
                                      </span>
                                      {won && (
                                        <span className="text-xs text-amber-600 dark:text-amber-400 font-semibold">Vencedor</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-span-3 hidden sm:block text-secondary truncate">{i.usuario.email}</div>
                                  <div className="col-span-2">
                                    <span className="px-2 py-0.5 bg-surface-container text-secondary rounded text-xs font-medium">
                                      {i.usuario.setor}
                                    </span>
                                  </div>
                                  <div className="col-span-2 text-right text-xs text-secondary">
                                    {format(new Date(i.inscrito_em), 'dd/MM HH:mm')}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
